import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Container, Row, Col, Alert, Button } from "react-bootstrap";
import Loader from "../../../components/widgets/Loader";
import axios from '../../../services/axios';
import { useStoreState, useStoreActions } from "easy-peasy";

const PipedriveSendingPage = ({ message, setMessage, sendStatus, autoSend = false, onBackToQualification }) => {

  const { apiData } = useStoreState(state => state.actionSlugs);
  const { applyClientsSync } = useStoreActions(actions => actions.clients);

  const [isSendingAxonaut, setIsSendingAxonaut] = useState(false);
  const [axonautError, setAxonautError] = useState(null);

  const [isSendingBO, setIsSendingBO] = useState(false);
  const [bOError, setBOError] = useState(null);

  const [isSendingPipedrive, setIsSendingPipedrive] = useState(false);
  const [pipedriveError, setPipedriveError] = useState(null);

  const formData = message?.newData;

  const hasToBeSentToAxonaut = message?.type === "deal";
  const canBeSentToBO = !hasToBeSentToAxonaut || message?.isAxonautSent;
  const canBeSentToPipedrive = message?.isBOSent;
  const syncIsDone = (!hasToBeSentToAxonaut || message?.isAxonautSent) && message?.isBOSent && message?.isPipedriveSent;
  // Editing the qualification is only safe while nothing has been written to any
  // external service — after the first send, the data must stay consistent with it.
  // Also hidden while a send is in flight: its outcome is not known yet.
  const nothingSentYet = !message?.isAxonautSent && !message?.isBOSent && !message?.isPipedriveSent
    && !isSendingAxonaut && !isSendingBO && !isSendingPipedrive;

  const sendToAxonaut = () => {
    const editAxonautUrl = apiData.ACTIONS?.pipedriveMessage?.updateAxonaut?.url?.replace('uniqueId', message?.uniqueId);
    const csrfToken = apiData.ACTIONS?.pipedriveMessage?.updateAxonaut?.csrfToken;
    setAxonautError(null);

    if (hasToBeSentToAxonaut) {
      setIsSendingAxonaut(true);
      axios.post(editAxonautUrl, { newData: formData, csrfToken })
        .then((data) => setMessage(data))
        .catch((e) => {
          if (e && typeof e === 'object' && typeof e['errors'] === 'string') {
            setAxonautError(e['errors'])
          } else if (typeof e === 'string') {
            setAxonautError(e)
          } else {
            setAxonautError("Une erreur s'est produite lors de l'envoi à Axonaut")
          }
        })
        .finally(() => setIsSendingAxonaut(false));
    } else {
      setAxonautError("Cette action n'a pas à être envoyée à Axonaut");
    }
  };

  const sendToBO = () => {
    const editBOUrl = apiData.ACTIONS?.pipedriveMessage?.updateBO?.url?.replace('uniqueId', message?.uniqueId);
    const csrfToken = apiData.ACTIONS?.pipedriveMessage?.updateBO?.csrfToken;
    setBOError(null);

    if (canBeSentToBO && !message?.isBOSent) {
      setIsSendingBO(true);
      axios.post(editBOUrl, { newData: formData, csrfToken })
        .then((data) => {
          setMessage(data);
          applyClientsSync(data?.clientsSync);
        })
        .catch((e) => {
          if (e && typeof e === 'object' && typeof e['errors'] === 'string') {
            setBOError(e['errors'])
          } else if (typeof e === 'string') {
            setBOError(e)
          } else {
            setBOError("Une erreur s'est produite lors de l'envoi au BO")
          }
        })
        .finally(() => setIsSendingBO(false));
    } else if (message?.isBOSent) {
      setBOError("Le backoffice M360 a déjà reçu ces informations");
    } else {
      setBOError("Cette action n'a pas encore été envoyée à Axonaut");
    }
  };

  const sendToPipedrive = () => {
    const editPipedriveUrl = apiData.ACTIONS?.pipedriveMessage?.updatePipedrive?.url?.replace('uniqueId', message?.uniqueId);
    const csrfToken = apiData.ACTIONS?.pipedriveMessage?.updatePipedrive?.csrfToken;
    setPipedriveError(null);

    if (canBeSentToPipedrive && !message?.isPipedriveSent) {
      setIsSendingPipedrive(true);
      axios.post(editPipedriveUrl, { newData: formData, csrfToken })
        .then((data) => setMessage(data))
        .catch((e) => {
          if (e && typeof e === 'object' && typeof e['errors'] === 'string') {
            setPipedriveError(e['errors'])
          } else if (typeof e === 'string') {
            setPipedriveError(e)
          } else {
            setPipedriveError("Une erreur s'est produite lors de l'envoi à Pipedrive")
          }
        })
        .finally(() => setIsSendingPipedrive(false));
    } else {
      setPipedriveError("Cette action n'a pas encore été envoyée au backoffice M360");
    }
  };

  useEffect(() => {
    if (!formData || !message) return;
    if (syncIsDone && message?.statusValue !== 6) {
      sendStatus(6);
      return;
    }
    // Only cascade automatically right after the operator confirmed (autoSend).
    // When reopening an already partially-processed message, external writes
    // (Axonaut invoice creation in particular) require an explicit retry click.
    if (!autoSend) return;
    if (hasToBeSentToAxonaut && !message.isAxonautSent && !axonautError && !isSendingAxonaut) {
      sendToAxonaut();
    } else if ((!hasToBeSentToAxonaut || message.isAxonautSent) && !message.isBOSent && !bOError && !isSendingBO) {
      sendToBO();
    } else if (message.isBOSent && !message.isPipedriveSent && !pipedriveError && !isSendingPipedrive) {
      sendToPipedrive();
    }
    // autoSend in the deps: the page can mount from the statusValue==5 routing clause
    // before step reaches 3 — the cascade must start when autoSend flips to true.
  }, [message, autoSend]);

  if (!formData || !message) {
    return <Container fluid>
      <Alert variant="danger">
        Informations manquantes pour procéder aux envois
      </Alert>
    </Container>
  }

  if (syncIsDone) {
    return <Container fluid>
      <Alert variant="success">
        Cette action a été transmise aux différents services.
      </Alert>
    </Container>
  }

  return (
    <Container fluid>
      <Row className="mb-3 align-items-center">
        <Col><h2 className="mb-0">{message.title ?? 'Message Pipedrive'}</h2></Col>
        {nothingSentYet && onBackToQualification && (
          <Col className="col-auto">
            <Button variant="outline-secondary" size="sm" onClick={onBackToQualification}>
              <i className="fas fa-edit me-1" />Modifier la qualification
            </Button>
          </Col>
        )}
      </Row>
      {hasToBeSentToAxonaut &&
        <><Row>
          <h3 className="mb-4 mt-4">Envoi à Axonaut</h3>
      </Row>
        {axonautError && <Alert variant="danger">{axonautError}</Alert>}
        {isSendingAxonaut ? <Loader />
          : (
            message?.isAxonautSent ?
              <p><span>&#9989;</span> Informations transmises à Axonaut</p>
              : <Row className="d-flex align-items-center">
                <Col className="col-auto">Envoyer les informations à Axonaut</Col>
                <Col className="col-auto">
                  <Button onClick={sendToAxonaut} size="sm" className="p-2 m-1"><i className="fas fa-redo" /></Button>
                </Col>
                </Row>)}
        </>}

      <><Row>
        <h3 className="mb-4 mt-4">Envoi au Backoffice M360</h3>
      </Row>
        {bOError && <Alert variant="danger">{bOError}</Alert>}
        {
          !canBeSentToBO ?
            <Alert variant='info'>Cette action ne peut pas encore être envoyée au BO.</Alert>
            : (isSendingBO ? <Loader />
          : (
            message?.isBOSent ?
              <p><span>&#9989;</span> Informations transmises au backoffice M360</p>
              : <Row className="d-flex align-items-center">
                <Col className="col-auto">Envoyer les informations au BO M360</Col>
                <Col className="col-auto">
                  <Button onClick={sendToBO} size="sm" className="p-2 m-1"><i className="fas fa-redo" /></Button>
                </Col>
                  </Row>))}
      </>

      <><Row>
        <h3 className="mb-4 mt-4">Envoi à Pipedrive</h3>
      </Row>
        {pipedriveError && <Alert variant="danger">{pipedriveError}</Alert>}
        {!canBeSentToPipedrive ?
          <Alert variant='info'>Cette action ne peut pas encore être envoyée à Pipedrive</Alert>
          : (isSendingPipedrive ? <Loader />
          : (
            message?.isPipedriveSent ?
              <p><span>&#9989;</span> Informations transmises à Pipedrive</p>
              : <Row className="d-flex align-items-center">
                <Col className="col-auto">Envoyer les informations à Pipedrive</Col>
                <Col className="col-auto">
                  <Button onClick={sendToPipedrive} size="sm" className="p-2 m-1"><i className="fas fa-redo" /></Button>
                </Col>
                </Row>))}
      </>
    </Container>
  )
};

PipedriveSendingPage.propTypes = {
  message: PropTypes.object.isRequired,
  setMessage: PropTypes.func.isRequired,
  sendStatus: PropTypes.func.isRequired,
  autoSend: PropTypes.bool,
  onBackToQualification: PropTypes.func
};

export default PipedriveSendingPage
